'use client'
import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid2 from "@mui/material/Grid2";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";

import {
  createUser,
  sendVerificationCode,
} from "_DatabaseFunctions/cloudCalls/auth";
import { validateAuthInput, getAuthErrorMessage, shouldRedirectToSignIn } from "utils/authErrorMessages";

import { auth } from "config/firebase";
import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";

interface SignUpProps {
  setSignin?: () => void;
}

export default function SignUp(props: SignUpProps) {
  const navigate = useNavigate();
  const [values, setValues] = useState({
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
    submitMsg: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!termsAccepted) {
      setValues({
        ...values,
        submitMsg: "You must accept the Terms of Service to create an account",
      });
      return;
    }

    // Use the improved validation
    const errs = validateAuthInput({
      email: values.email,
      password: values.password,
      confirmPassword: values.confirmPassword,
      displayName: values.displayName,
    });

    setErrors(errs);
    if (Object.keys(errs).length) {
      setValues({ ...values, submitMsg: "Please fix the errors below and try again" });
    } else {
      setIsLoading(true);
      setValues({ ...values, submitMsg: "" });
      signUserUp();
    }
  };

  const signUserUp = async () => {
    try {
      // Create user account
      const user = await createUser({
        email: values.email,
        password: values.password,
        displayName: values.displayName,
      });
      console.log("User created:", user);

      // Automatically sign in the user first
      console.log("Signing in user automatically...");
      await signInWithEmailAndPassword(auth, values.email, values.password);

      // Wait for auth state to update, then send verification code and redirect
      const unsubscribe = onAuthStateChanged(auth, async (authenticatedUser) => {
        if (authenticatedUser && authenticatedUser.email === values.email) {
          console.log("User authenticated, sending verification code...");
          
          try {
            // Now send verification code with authenticated user context
            await sendVerificationCode({
              userId: authenticatedUser.uid,
              email: authenticatedUser.email,
            });
            console.log("Verification code sent successfully");
          } catch (codeError) {
            console.error("Error sending verification code:", codeError);
            // Don't block the flow if verification code fails
          }
          
          unsubscribe(); // Clean up the listener

          console.log("Redirecting to home...");
          // Small delay to show success message before redirect
          setTimeout(() => {
            navigate("/home");
          }, 1500);
        }
      });

      setValues({
        ...values,
        submitMsg: `Account created successfully! You've been logged in and a verification code has been sent to ${values.email}. Redirecting...`,
      });
    } catch (err: any) {
      console.error("Error during signup/signin process:", err);

      // Use improved error handling
      const userFriendlyMessage = getAuthErrorMessage(err);

      // Check if the error suggests the user should sign in instead
      if (shouldRedirectToSignIn(err)) {
        setValues({
          ...values,
          submitMsg: `${userFriendlyMessage} Would you like to sign in instead?`,
        });
      } else if (
        err.code === "auth/user-not-found" ||
        err.code === "auth/invalid-credential"
      ) {
        setValues({
          ...values,
          submitMsg:
            "Account created successfully! Please sign in with your credentials to continue.",
        });
      } else {
        setValues({
          ...values,
          submitMsg: userFriendlyMessage,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Avatar
        sx={{
          mt: 3,
          mb: 2,
          backgroundColor: "primary.main",
        }}
      />
      <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
        Sign Up
      </Typography>
      <Box component="form" noValidate sx={{ width: "100%" }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <TextField
              value={values.displayName}
              onChange={handleInputChange}
              variant="outlined"
              required
              fullWidth
              id="displayName"
              label="Display Name"
              name="displayName"
              autoComplete="displayName"
              helperText={errors.displayName}
              error={!!errors.displayName}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              value={values.email}
              onChange={handleInputChange}
              variant="outlined"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              helperText={errors.email}
              error={!!errors.email}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              value={values.password}
              onChange={handleInputChange}
              variant="outlined"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              helperText={errors.password}
              error={!!errors.password}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              value={values.confirmPassword}
              onChange={handleInputChange}
              variant="outlined"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="password"
              autoComplete="current-password"
              helperText={errors.confirmPassword}
              error={!!errors.confirmPassword}
            />
          </Grid2>
          <Grid2 size={12}>
            <Box sx={{ mt: 2, mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    name="termsAccepted"
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I have read and agree to the{" "}
                    <Link href="/terms" target="_blank" rel="noopener">
                      Terms of Service
                    </Link>
                    , including compliance with all local laws regarding poker
                    games
                  </Typography>
                }
              />
            </Box>
          </Grid2>
        </Grid2>
        {isLoading ? (
          <>
            <div style={{ color: "red" }}>Creating Account...</div>
            <CircularProgress />
          </>
        ) : (
          <Button
            type="submit"
            onClick={handleSubmit}
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
          >
            Sign Up
          </Button>
        )}
        {values.submitMsg && (
          <Typography
            variant="body2"
            color={values.submitMsg.includes("successfully") || values.submitMsg.includes("Redirecting") ? "success" : "error"}
            sx={{ mt: 2, textAlign: "center" }}
          >
            {values.submitMsg}
          </Typography>
        )}
        <Grid2 container justifyContent="flex-end" sx={{ mt: 2 }}>
          <Grid2>
            <Link 
              component={RouterLink} 
              to="/signin" 
              variant="body2"
              onClick={props.setSignin}
            >
              Already have an account? Sign in
            </Link>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
}
